import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Paper,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { CloseOutlined, InfoOutlined, MoreVert } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FerramentaDetalhe } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useProfile } from "../../../context/ProfileContext";
import { useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import {
	clientContractService,
	IListagemClientContractByContract,
	TClientContractByContractPromise,
} from "../../../shared/services/api/client-contract/clientContractService";
import { Environment } from "../../../shared/environment";
import { formatDateBRHasMinutes } from "../../../shared/utils/format-date";
import { formatClientContractStatus } from "../../../shared/utils/formatStatusSignature";
import { globalMaskDocument } from "../../../shared/utils";
import { returnMessageError } from "../../../shared/utils/returnMessageError";

export const ListagemDetalhesContratoPresentation: React.FC = () => {
	const { id } = useParams<"id">();
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const toastHandler = useToast();
	const { permissions } = useProfile();

	const [isLoading, setIsLoading] = useState(true);
	const [rows, setRows] = useState<IListagemClientContractByContract[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [contractTitle, setContractTitle] = useState<string>("");
	const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
	const [actionsRow, setActionsRow] = useState<IListagemClientContractByContract | null>(null);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [statusDraft, setStatusDraft] = useState<string>("todos");

	const hasContractsOptionsModule = useMemo(() => {
		return Object.prototype.hasOwnProperty.call(permissions ?? {}, PermissionRolesEnum.ContractsOptions);
	}, [permissions]);

	const canAccess = useMemo(() => {
		if (hasContractsOptionsModule) {
			return checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsDetails);
		}
		return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Contracts);
	}, [hasContractsOptionsModule]);

	useEffect(() => {
		let isMounted = true;

		const load = async () => {
			if (!id) {
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				const pagina = Number(searchParams.get("pagina") || "1");
				const rowsPerPage = Number(searchParams.get("rowsPerPage") || "10");
				const status = searchParams.get("status") || "todos";
				const result: TClientContractByContractPromise = await clientContractService.getAllByContractId(id, pagina, rowsPerPage, status);
				if (!isMounted) return;

				if (result instanceof Error) throw result;

				setRows(result.results ?? []);
				setTotalCount(result.total ?? 0);
				setContractTitle(result.results?.[0]?.client_contract_title ?? result.results?.[0]?.title ?? "");
			} catch (err: any) {
				if (!isMounted) return;
				const { titleMessage, contentMessage } = returnMessageError(err);
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: (
						<Box>
							<Typography fontWeight={700}>{titleMessage}</Typography>
							<Typography variant="body2">{contentMessage}</Typography>
						</Box>
					),
				});
			} finally {
				if (!isMounted) return;
				setIsLoading(false);
			}
		};

		load();

		return () => {
			isMounted = false;
		};
	}, [id, searchParams]);

	useEffect(() => {
		setStatusDraft(searchParams.get("status") || "todos");
	}, [searchParams]);

	useEffect(() => {
		if (!canAccess) {
			navigate("/dashboard", { replace: true });
		}
	}, [canAccess, navigate]);

	if (!canAccess) return null;

	const pagina = Number(searchParams.get("pagina") || "1");
	const rowsPerPage = Number(searchParams.get("rowsPerPage") || "10");

	const actionsMenuOpen = Boolean(actionsAnchorEl);

	const openActionsMenu = (event: React.MouseEvent<HTMLElement>, row: IListagemClientContractByContract) => {
		setActionsAnchorEl(event.currentTarget);
		setActionsRow(row);
	};

	const closeActionsMenu = () => {
		setActionsAnchorEl(null);
	};

	const openDetailsModal = () => {
		setDetailsOpen(true);
	};

	const closeDetailsModal = () => {
		setDetailsOpen(false);
		setActionsRow(null);
	};

	const getValue = (row: IListagemClientContractByContract | null, key1: keyof IListagemClientContractByContract, key2?: keyof IListagemClientContractByContract) => {
		if (!row) return undefined;
		const v1 = row[key1];
		if (v1 !== null && v1 !== undefined && v1 !== "") return v1 as any;
		if (!key2) return v1 as any;
		return row[key2] as any;
	};

	const parseSignersCopies = (value: unknown): string[] => {
		if (!value) return [];
		const raw = String(value).trim();
		if (!raw) return [];
		return raw
			.split(/[,;\n\t ]+/g)
			.map((v) => v.trim())
			.filter(Boolean);
	};

	const updateParams = (updates: Record<string, string>) => {
		const next = new URLSearchParams(searchParams);
		Object.entries(updates).forEach(([k, v]) => next.set(k, v));
		setSearchParams(next, { replace: true });
	};

	const signatureStatusOptions = useMemo(
		() => [
			{ value: "todos", label: "Todos" },
			{ value: "signed_signature", label: "Assinado" },
			{ value: "pending_signature", label: "Pendente" },
			{ value: "not_signed_signature", label: "Não assinado" },
			{ value: "canceled_signature", label: "Cancelado" },
		],
		[],
	);

	const signatureStatusChipSx = (status: string) => {
		return (theme: any) => {
			switch (status) {
				case "signed_signature":
					return {
						color: theme.palette.success.dark,
						fontWeight: 500,
						background: alpha(theme.palette.success.main, 0.12),
						border: `0.5px solid ${theme.palette.divider}`,
					};
				case "pending_signature":
					return {
						color: theme.palette.info.dark,
						fontWeight: 500,
						background: alpha(theme.palette.info.main, 0.12),
						border: `0.5px solid ${theme.palette.divider}`,
					};
				case "not_signed_signature":
					return {
						color: theme.palette.warning.dark,
						fontWeight: 500,
						background: alpha(theme.palette.warning.main, 0.16),
						border: `0.5px solid ${theme.palette.divider}`,
					};
				case "canceled_signature":
					return {
						color: theme.palette.error.dark,
						fontWeight: 500,
						background: alpha(theme.palette.error.main, 0.12),
						border: `0.5px solid ${theme.palette.divider}`,
					};
				default:
					return {
						color: theme.palette.text.primary,
						fontWeight: 500,
						background: theme.palette.grey[100],
						border: `0.5px solid ${theme.palette.divider}`,
					};
			}
		};
	};

	return (
		<LayoutBaseDePagina
			titulo="Contratos"
			subtitulo={contractTitle ? `Disparos do contrato • ${contractTitle}` : "Disparos do contrato"}
			barraDeFerramentas={
				<FerramentaDetalhe
					showButtonNew={false}
					showBotaoApagar={false}
					showButtonFollowUp={false}
					showBotaoSalvar={false}
					showBotaoSalvarFechar={false}
					clicarVoltar={() => navigate("/clientes/contratos")}
				/>
			}
		>
			<LoadingComp isopen={isLoading} />

			<Box sx={{ width: "100%" }}>
				<TableContainer
					component={Paper}
					variant="elevation"
					sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
				>
					<Typography variant="h6" color="primary" mb={2}>
						{contractTitle ? `Disparos para o contrato ${contractTitle}` : "Disparos do contrato"}
					</Typography>

					<Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={2}>
						<TextField
							label="Status"
							select
							size="small"
							value={statusDraft}
							onChange={(e) => setStatusDraft(String(e.target.value))}
							sx={{ minWidth: 220 }}
							disabled={isLoading}
						>
							{signatureStatusOptions.map((opt) => (
								<MenuItem key={opt.value} value={opt.value}>
									{opt.label}
								</MenuItem>
							))}
						</TextField>

						<Button
							variant="contained"
							color="primary"
							disabled={isLoading}
							onClick={() => updateParams({ pagina: "1", status: statusDraft })}
						>
							Filtrar
						</Button>

						<Button
							variant="outlined"
							disabled={isLoading}
							onClick={() => {
								setStatusDraft("todos");
								updateParams({ pagina: "1", status: "todos" });
							}}
						>
							Limpar
						</Button>
					</Box>

					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow sx={{ backgroundColor: "grey.100" }}>
								<TableCell>
									<Typography variant="button">Cliente</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="button">CPF/CNPJ</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="button">Assinatura</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="button">Assinado em</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="button">Disparado em</Typography>
								</TableCell>
								<TableCell align="right">
									<Typography variant="button">Ações</Typography>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.length === 0 && !isLoading && (
								<TableRow>
									<TableCell colSpan={6} align="center">
										Nenhum registro
									</TableCell>
								</TableRow>
							)}

							{rows.map((row) => {
								const statusSignature = row.client_contract_status_signature ?? row.status_signature;
								const signedAt = row.client_contract_signed_date_at ?? row.signed_date_at;
								const createdAt = row.client_contract_created_at ?? row.created_at;
								const rowId = row.client_contract_id ?? row.id;

								return (
									<TableRow key={rowId} hover>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{row.client_name}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" color="text.secondary">
												{globalMaskDocument(row.client_document ?? "")}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Chip
												size="small"
												label={formatClientContractStatus(statusSignature)}
												sx={signatureStatusChipSx(statusSignature)}
											/>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" color="text.secondary">
												{signedAt ? formatDateBRHasMinutes(signedAt) : "-"}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" color="text.secondary">
												{createdAt ? formatDateBRHasMinutes(createdAt) : "-"}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Tooltip title="Ações" placement="top">
												<span>
													<IconButton disabled={isLoading} size="small" onClick={(e) => openActionsMenu(e, row)}>
														<MoreVert fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>

					<Menu
						anchorEl={actionsAnchorEl}
						open={actionsMenuOpen}
						onClose={closeActionsMenu}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						<MenuItem
							disabled={isLoading || !actionsRow}
							onClick={() => {
								closeActionsMenu();
								openDetailsModal();
							}}
						>
							<ListItemIcon>
								<InfoOutlined fontSize="small" sx={{ color: "primary.main" }} />
							</ListItemIcon>
							<ListItemText>Detalhar assinatura</ListItemText>
						</MenuItem>
					</Menu>

					<Dialog open={detailsOpen} onClose={closeDetailsModal} fullWidth maxWidth="md">
						<DialogTitle
							sx={{
							color: "primary.main",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 2,
						}}
						>
							<Typography variant="h6" color="primary">
								Detalhes da assinatura
							</Typography>
							<IconButton aria-label="Fechar" onClick={closeDetailsModal} size="small">
								<CloseOutlined fontSize="small" />
							</IconButton>
						</DialogTitle>
						<DialogContent dividers>
							{actionsRow ? (
								<Box>
									<Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
										<Box>
											<Typography variant="subtitle1" fontWeight={600}>
												{String(getValue(actionsRow, "client_name", "client_name") ?? "")}
											</Typography>
											<Typography variant="body2" color="text.secondary">
												{globalMaskDocument(String(getValue(actionsRow, "client_document", "client_document") ?? ""))}
											</Typography>
										</Box>
										<Chip
											size="small"
											label={formatClientContractStatus(String(getValue(actionsRow, "client_contract_status_signature", "status_signature") ?? ""))}
											sx={signatureStatusChipSx(String(getValue(actionsRow, "client_contract_status_signature", "status_signature") ?? ""))}
										/>
									</Box>

									<Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
										<Box>
											<Typography variant="body2" color="text.secondary">Contrato</Typography>
											<Typography variant="body1">
												{String(getValue(actionsRow, "client_contract_title", "title") ?? "-")}
											</Typography>
										</Box>

										<Box>
											<Typography variant="body2" color="text.secondary">E-mails em cópia</Typography>
											{(() => {
												const copiesRaw = getValue(actionsRow, "client_contract_signers_copies", "signers_copies");
												const copies = parseSignersCopies(copiesRaw);
												if (copies.length === 0) {
													return <Typography variant="body1">-</Typography>;
												}

												return (
													<Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
														{copies.map((email) => (
															<Chip
																key={email}
																size="small"
																variant="outlined"
																label={email}
																sx={{ fontWeight: 500 }}
															/>
														))}
													</Box>
												);
											})()}
										</Box>

										<Box>
											<Typography variant="body2" color="text.secondary">Doc key</Typography>
											<Typography variant="body1">
												{String(getValue(actionsRow, "client_contract_doc_key", "doc_key") ?? "-")}
											</Typography>
										</Box>

										<Box>
											<Typography variant="body2" color="text.secondary">Storage</Typography>
											<Typography variant="body1">
												{String(getValue(actionsRow, "client_contract_storage", "storage") ?? "-")}
											</Typography>
										</Box>

										<Box>
											<Typography variant="body2" color="text.secondary">Enviado em</Typography>
											<Typography variant="body1">
												{(() => {
													const createdAt = getValue(actionsRow, "client_contract_created_at", "created_at");
													return createdAt ? formatDateBRHasMinutes(String(createdAt)) : "-";
												})()}
											</Typography>
										</Box>

										<Box>
											<Typography variant="body2" color="text.secondary">Assinado em</Typography>
											<Typography variant="body1">
												{(() => {
													const signedAt = getValue(actionsRow, "client_contract_signed_date_at", "signed_date_at");
													return signedAt ? formatDateBRHasMinutes(String(signedAt)) : "-";
												})()}
											</Typography>
										</Box>

										<Box>
											<Typography variant="body2" color="text.secondary">Upload em</Typography>
											<Typography variant="body1">
												{(() => {
													const uploadedAt = getValue(actionsRow, "client_contract_uploaded_at", "uploaded_at");
													return uploadedAt ? formatDateBRHasMinutes(String(uploadedAt)) : "-";
												})()}
											</Typography>
										</Box>
									</Box>

									<Box mt={2}>
										<Typography variant="body2" color="text.secondary">IDs</Typography>
										<Typography variant="body2" color="text.secondary">
											ClientContract: {String(getValue(actionsRow, "client_contract_id", "id") ?? "-")}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Contrato: {String(getValue(actionsRow, "client_contract_contractId", "contractId") ?? "-")}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Cliente: {String(getValue(actionsRow, "client_contract_clientId", "clientId") ?? "-")}
										</Typography>
									</Box>
								</Box>
							) : null}
						</DialogContent>
						<DialogActions>
							<Button onClick={closeDetailsModal}>Fechar</Button>
						</DialogActions>
					</Dialog>

					<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2}>
						<TablePagination
							component="div"
							count={totalCount}
							page={Math.max(pagina - 1, 0)}
							rowsPerPage={rowsPerPage}
							onPageChange={(_, newPage) => updateParams({ pagina: String(newPage + 1) })}
							onRowsPerPageChange={(e) => updateParams({ pagina: "1", rowsPerPage: e.target.value })}
							rowsPerPageOptions={[10, 25, 50]}
							labelRowsPerPage="Linhas por página:"
							labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
						/>
					</Box>
				</TableContainer>
			</Box>
		</LayoutBaseDePagina>
	);
};
